import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, ClipboardCheck, Loader2, Copy, Save } from "lucide-react";
import { toast } from "sonner";
import { api, streamSSE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export function AIAssistant({ pratica, onSaved }) {
  const [sezioni, setSezioni] = useState([]);
  const [sezione, setSezione] = useState("relazione_tecnica");
  const [istruzioni, setIstruzioni] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const outRef = useRef();

  useEffect(() => {
    api.get("/ai/sezioni").then((r) => setSezioni(r.data));
    api.get(`/pratiche/${pratica.id}/ai/history`).then((r) => setHistory(r.data.filter((m) => m.role === "assistant")));
  }, [pratica.id]);

  useEffect(() => { if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight; }, [output]);

  const run = async (path, body) => {
    setBusy(true); setOutput("");
    let acc = "";
    try {
      await streamSSE(path, body, (d) => { acc += d; setOutput(acc); });
      const r = await api.get(`/pratiche/${pratica.id}/ai/history`);
      setHistory(r.data.filter((m) => m.role === "assistant"));
    } catch (err) { toast.error(err.message || "Errore AI"); } finally { setBusy(false); }
  };

  const save = async () => {
    try {
      const { data } = await api.patch(`/pratiche/${pratica.id}`, { sezioni: { ...(pratica.sezioni || {}), [sezione]: output } });
      onSaved(data); toast.success("Bozza salvata nella pratica");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const label = sezioni.find((s) => s.key === sezione)?.label || sezione;

  return (
    <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-5" data-testid="ai-assistant">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-[#003087] text-white flex items-center justify-center"><Sparkles size={16} /></div>
        <div>
          <h3 className="font-heading font-medium text-slate-900">Assistente AI</h3>
          <p className="text-xs text-slate-500">Bozze di sezioni e controllo completezza basati sui dati della pratica</p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <select value={sezione} onChange={(e) => setSezione(e.target.value)} className="h-10 w-full rounded-md border border-indigo-200 bg-white px-3 text-sm" data-testid="ai-section-select">
          {sezioni.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <Button disabled={busy} onClick={() => run(`/pratiche/${pratica.id}/ai/draft`, { sezione, istruzioni })} className="w-full bg-[#003087] hover:bg-[#0070BA]" data-testid="ai-draft-button">
          {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <Sparkles size={15} className="mr-2" />} Genera bozza
        </Button>
      </div>
      <Textarea placeholder="Istruzioni aggiuntive (opzionali): es. 180 ombrelloni, fotovoltaico 30 kW, 12 dipendenti stagionali…" value={istruzioni} onChange={(e) => setIstruzioni(e.target.value)} rows={2} className="mt-2 bg-white border-indigo-200" data-testid="ai-instructions-input" />
      <Button variant="outline" disabled={busy} onClick={() => run(`/pratiche/${pratica.id}/ai/check`)} className="mt-2 w-full border-indigo-200 bg-white" data-testid="ai-check-button">
        <ClipboardCheck size={15} className="mr-2" /> Controllo completezza fascicolo
      </Button>

      {(output || busy) && (
        <div className="mt-4 bg-white border border-indigo-100 rounded-lg" data-testid="ai-output">
          <div className="flex items-center justify-between px-4 py-2 border-b border-indigo-100">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(output); toast.success("Copiato"); }} disabled={!output} data-testid="ai-copy-button"><Copy size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={save} disabled={!output || busy} data-testid="ai-save-button"><Save size={14} className="mr-1" /> Salva</Button>
            </div>
          </div>
          <div ref={outRef} className={`prose-ai px-4 py-3 text-sm max-h-[420px] overflow-y-auto ${busy ? "caret" : ""}`}>
            <ReactMarkdown>{output}</ReactMarkdown>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <details className="mt-4" data-testid="ai-history">
          <summary className="text-xs font-medium text-slate-600 cursor-pointer">Cronologia generazioni ({history.length})</summary>
          <div className="mt-2 space-y-1">
            {history.slice().reverse().map((m) => (
              <button key={m.id} onClick={() => setOutput(m.content)} className="w-full text-left text-xs px-3 py-2 rounded-md bg-white border border-indigo-100 hover:bg-indigo-50 transition-colors">
                <span className="font-medium text-slate-800">{m.tipo}</span> · {new Date(m.created_at).toLocaleString("it-IT")}
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
